import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Navbar from "react-bootstrap/Navbar";

import ModalBody from "../../../common/ModalBody";
import ModalHeader from "../../../common/ModalHeader";
import { getCurrentDate } from "../../../../common/Validations";
import {
  getJobcardReportFooter,
  loadJobcardRevisingMetadata,
  REVISING_REPORT_TYPES,
} from "../../../../common/JobcardPrintUtils";

const QUALITY_CERTIFICATE_REPORT_TITLE = "TM/QC/F/01";

const firstNonEmpty = (...values) =>
  values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== "",
  ) ?? "";

const normalizeJobcard = (jobcard) => String(jobcard ?? "").trim();

const PrintMultipleQCReports = (props) => {
  const { t } = useTranslation();
  const [revisingMetadata, setRevisingMetadata] = useState({});
  const jobcardRowsByJobcard = useMemo(
    () =>
      new Map(
        (props.jobcardRows || []).map((row) => [
          normalizeJobcard(row?.jobcard),
          row,
        ]),
      ),
    [props.jobcardRows],
  );

  useEffect(() => {
    if (props.reportList?.length) {
      loadJobcardRevisingMetadata(
        props.reportList,
        REVISING_REPORT_TYPES.QUALITY_CERTIFICATE,
      )
        .then(setRevisingMetadata)
        .catch((error) => {
          console.error("Error loading jobcard revising metadata:", error);
          setRevisingMetadata({});
        });
    } else {
      setRevisingMetadata({});
    }
  }, [props.reportList]);

  function applyPrintStyles() {
    const style = document.createElement("style");
    style.textContent = ``;
    document.head.appendChild(style);
  }

  window.onbeforeprint = applyPrintStyles;

  const printModalContent = () => {
    const printWindow = window.open("", "", "height=842,width=1295");
    const modalContent = document.getElementById("printable-area").innerHTML;

    printWindow.document.write("<html><head><title>Print</title>");
    printWindow.document.write(
      '<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css">'
    );
    printWindow.document.write("<style>");
    printWindow.document.write(`

      @media print {
        /* Add general page margin to add space around the content */

        .bi-plus-slash-minus::before {
          content: "+/-";  /* or any symbol/text you want */
          font-family: inherit;
        }

        @page {
          size: A4 portrait;  /* Ensure the page size is A4 in portrait mode */
          margin: 1in;  /* Set a 1-inch margin around the entire page (can be adjusted) */
        }

        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
         .report-title-line {
          display: flex !important;
          flex-direction: row !important;
          justify-content: space-between;
          width: 95%;
          white-space: nowrap;
          margin-top:5px;
          margin-left:20px;
        }

        .report-title-line > div {
          flex: 1;
          padding: 0;
          margin: 0;
          text-align: left;
        }

        .report-title-line > div:last-child {
          text-align: right;
        }

        .page-break {
          page-break-before: always; /* Forces content to start on a new page */
           break-before: page;        /* Modern browsers */
        }

        .page-break  {
          margin: 5mm; /* Might not work alone */
          padding: 5mm; /* More reliable for visible space */
        }

        .page-break tr,
        .page-break td {
          height: 30px !important;
        }


        /* Table Styling */
        table.jobcard-content-table {
          width: 100%; /* Ensure the table takes up full width */
          border-collapse: collapse; /* Collapse the table borders */
          page-break-before: always; /* Force a page break before this table */
          padding: 10px; /* Add padding inside the table */
        }
          .jobcard-content-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }

        .telma-header {
          text-align: center;
          text-decoration: underline;
        }
          .telma-detail {
            display:block;
            text-align: center;
            width:100%;
          }
            .jobcard-singlerow {
            text-align: center !important;
          }

        table.jobcard-content-table td, table.jobcard-content-table th {
          border: 2px solid #000000; /* Add borders to the table cells */
          padding: 5px 8px; /* Padding inside the cells to give some space */
          text-align: left; /* Align text to the left */
        }

        /* QR Code cell (with rowspan) styling */
        .qr-code-cell {
          padding: 10px; /* Padding inside the QR code cell */
          text-align: center; /* Center the QR code */
          vertical-align: middle; /* Center QR code vertically */
        }

        /* Styling for text content and card content */
        .card-body {
          padding: 20px; /* Add padding inside the card body */
        }

        /* Prevent content from breaking into multiple pages */
        table.jobcard-content-table tr {
          page-break-inside: avoid; /* Prevent rows from breaking across pages */
        }

        .jobcard-detail-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed; /* Equal column widths */
          font-size: 11px;

          th:first-child,
          td:first-child {
            width: 16%; /* First column gets more space */
          }

          th:not(:first-child),
          td:not(:first-child) {
            width: 10%; /* Adjust based on how many columns you have */
          }
        }

          .jobcard-detail-table th,
            .jobcard-detail-table td {
              border: 2px solid #000;
              padding: 4px;
              word-wrap: break-word;
              text-align: left;
              height: 40px;
            }

        /* Job card content */
        .jobcard-content-bold {
          font-weight: bold;
        }

        /* Additional spacing between the QR code and text */
        .jobcard-qr-code {
          padding: 5px; /* Add padding around the QR code */
        }

        /* Add padding to any text elements inside the table */
        .jobcard-title-content, .jobcard-priority, .jobcard-content-bold {
          padding-bottom: 2px; /* Provide some space between lines of text */
        }

        /* Add spacing between the rows of the table */
        tr + tr {
          margin-top: 10mm; /* Adds space between each row */
        }

        /* Add space around the body content */
        body {
          padding: 20px; /* Padding around the entire page content */
          margin: 0;
        }

          .jobcard-qr-code {
          display: flex; /* Use flexbox to center content */
          justify-content: center; /* Center horizontally */
          align-items: center; /* Center vertically */
          width: 100%; /* Ensure the QR code container takes full width */
          height: auto; /* Adjust the height based on content */
          padding: 5px; /* Optional: Add some padding around the QR code */
          margin: 0 auto; /* Center the QR code container in the page */
        }

        /* You can also set a fixed size for QR code to make sure it scales well */
        .qr-code {
          max-width: 120px; /* Max width of the QR code */
          width: 100%; /* Make sure the QR code fits inside its container */
          height: auto; /* Maintain aspect ratio */
        }
          .jobcard-priority {
          color: #0047ab !important; /* Default priority color */
          text-align: center;
          font-weight:bold !important;
        }

        .jobcard-priority-urgent {
          color: red !important; /* Priority 'urgent' color */
          text-align: center;
          font-weight:bold;
          font-weight:bold !important;
        }

        .jobcard-title-content {
          text-align: center !important;
          font-weight: bold;
          color: #0047ab;
          margin-bottom:2px;
        }

        .jobcard-title-content-jobcard {
          margin-top:10px;
          text-align: center !important;
          font-weight: bold;
          color: #0047ab;
        }
        .qc-sign-1 {
          width: 170px;
          height: 80px;
          margin-bottom: 25px;
        }
        .qc-sign-2 {
          width: 140px;
          height: 80px;
          margin-bottom: 25px;
        }
      }

    `);
    printWindow.document.write("</style>");
    printWindow.document.write("</head><body>");
    printWindow.document.write(modalContent);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const { onHide } = props;
  return (
    <Modal
      {...props}
      onHide={onHide}
      animation={false}
      dialogClassName="modal-90w"
      backdrop="static"
    >
      <ModalHeader
        title={` ${t(
          "msg_reports.quality_certificate.quality_certificate_report_for_jobcard"
        )} `}
      ></ModalHeader>
      <ModalBody>
        <Navbar expand="lg" className="qr-code-navbar">
          <Button
            class="accordion-button"
            type="button"
            variant="primary"
            onClick={printModalContent}
          >
            <i className="bi bi-printer"></i> {"Print"}
          </Button>
        </Navbar>
        <div id="printable-area">
          <Row>
            {props.reportList.map((report, index) => {
              const matchingJobcardRow = jobcardRowsByJobcard.get(
                normalizeJobcard(report?.jobcard),
              );
              const sizeCode = firstNonEmpty(
                matchingJobcardRow?.sizeCode,
                report?.sizeCode,
              );
              const sizeName = firstNonEmpty(
                matchingJobcardRow?.sizeName,
                report?.sizeName,
              );

              return (
                <React.Fragment key={report?.jobcard || index}>
                  <Col xs={12} sm={12} md={12} lg={12} className="mb-4">
                  <table className="jobcard-content-table">
                    <thead>
                      <th colSpan="6">
                        <h4 className="telma-header">
                          TELMA MICRO NEEDLES PVT LTD
                        </h4>
                        <span className="telma-detail">
                          <p>
                            NO-56/6, MAJARA HOSAHALLI MAIN ROAD, <br></br>
                            DODDABALLAPURA INDUSTRIAL AREA,<br></br>
                            BANGALORE-561203
                          </p>
                        </span>
                      </th>
                    </thead>

                    <tbody>
                      <tr>
                        <td colSpan="6">
                          <h4 className="telma-header">QUALITY CERTIFICATE</h4>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="2">Needle Code:</td>
                        <td colSpan="1">{sizeCode || "-"}</td>
                        <td colSpan="1">Description:</td>
                        <td colSpan="2">{sizeName || "-"}</td>
                      </tr>
                      <tr>
                        <td colSpan="2">Batch No:</td>
                        <td colSpan="1">{report?.jobcard?.split("_")?.[0] }</td>
                        <td colSpan="1">Batch Qty No:</td>
                        <td colSpan="2">{report?.originalQuantityPerJobCard}</td>
                      </tr>
                      <tr>
                        <td colSpan="2">Date:</td>
                        <td colSpan="1">
                          <span>{ report?.reportDate ? report.reportDate : getCurrentDate()}</span>
                        </td>
                        <td colSpan="1">Sample Qty</td>
                        <td colSpan="2">5</td>
                      </tr>
                      <tr>
                        <td colSpan="2"> </td>
                        <td colSpan="1"> </td>
                        <td colSpan="1"> </td>
                        <td colSpan="2"> </td>
                      </tr>
                      <tr>
                        <td colSpan="1">SI No.</td>
                        <td colSpan="1">Tests</td>
                        <td colSpan="1">Specifications</td>
                        <td colSpan="1">Tolerance</td>
                        <td colSpan="1">Observations</td>
                        <td>Complies</td>
                      </tr>
                      <tr>
                        <td rowSpan="6">1</td>
                        <td height={"80px"}>Dimensions And Curvature</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                      <tr>
                        <td>a. Length</td>
                        <td>{report.qcSizeCodes?.[1]}</td>
                        <td><i className="bi bi-plus-slash-minus pr-2"></i> 1.000 mm</td>
                        <td>
                          <div>
                            <p className="m-0 p-0 fw-datatable-row">
                              {report.dimensionObservation}{" "}
                            </p>
                          </div>
                        </td>
                        <td>{report.dimensionsComplies}</td>
                      </tr>
                      <tr>
                        <td>b. Bore diameter</td>
                        <td>{parseInt(report.qcSizeCodes?.[2]) * parseInt(10)}</td>
                        <td>
                          &nbsp;- 0.020 mm <br/>
                          + 0.030mm
                        </td>
                        <td>
                          <div>
                            <p className="m-0 p-0 pt-1 fw-datatable-row">
                              {report.boreDiameterObservation}{" "}
                            </p>
                          </div>
                        </td>
                        <td>{report.boreDiameterComplies}</td>
                      </tr>
                      <tr>
                        <td>c. Wire diameter</td>
                        <td>{parseInt(report.qcSizeCodes?.[3]) * parseInt(10)}</td>
                        <td><i className="bi bi-plus-slash-minus pr-2"></i> 0.020 mm</td>
                        <td>
                          <div>
                            <p className="m-0 p-0 pt-1 fw-datatable-row">
                              {report.wireDiameterObservation}{" "}
                            </p>
                          </div>
                        </td>
                        <td>{report.wireDiameterComplies}</td>
                      </tr>
                      <tr>
                        <td>d. Bore depth</td>
                        <td>{report.boreDepth}</td>
                        <td><i className="bi bi-plus-slash-minus pr-2"></i> 0.200 mm</td>
                        <td>
                          <div>
                            <p className="m-0 p-0 pt-1 fw-datatable-row">
                              {report.boreDepthObservation}{" "}
                            </p>
                          </div>
                        </td>
                        <td>{report.boreDepthComplies}</td>
                      </tr>
                      <tr>
                        <td height={"80px"}>e. Curvature</td>
                        <td>Match With Curvature Template</td>
                        <td>Matching With Curvature Template</td>
                        <td>Complies With Curvature Template</td>
                        <td>Yes</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>Surface Finish</td>
                        <td>Smooth Finish</td>
                        <td>Pit Free Surface</td>
                        <td>Observed Pit Free Surface Finish</td>
                        <td>{report.surfaceFinishComplies}</td>
                      </tr>
                      <tr>
                        <td>3</td>
                        <td>Siliconisation</td>
                        <td>
                          coating the surface of a needle with a thin layer
                        </td>
                        <td>Aluminum Oxide powder Test</td>
                        <td>
                          Aluminum Oxide powder adheres to the needles
                        </td>
                        <td>{report.siliconisationComplies}</td>
                      </tr>
                      <tr>
                        <td>4</td>
                        <td>Penetration Test</td>
                        <td>Consistency Of Penetration</td>
                        <td>
                          <p>
                            Min-0.50Kgs <br></br>Max-2Kgs
                          </p>
                        </td>
                        <td>
                          <div>
                            <p className="m-0 p-0 pt-1 fw-datatable-row">
                              {report.pentrationObservation}{" "}
                            </p>
                          </div>
                        </td>
                        <td>{report.penetrationTestComplies}</td>
                      </tr>
                      <tr>
                        <td>STATUS</td>
                        <td>
                          <span className="fw-bold-m">APPROVED</span>
                        </td>
                        <td colSpan="2">APPROVED QY NOS:</td>
                        <td colSpan="2">
                          <span className="fw-bold-m">
                            {report?.approvedQuantity}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="1">Remarks</td>
                        <td colSpan="5">{report.remarks}</td>
                      </tr>
                      <tr>
                        <td colSpan="6" height="50">
                          <b>Note: Length And Bore Depth are in Millimeters, Bore Diameter And Wire Diameter are in Microns.</b>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="6">
                          <Row>
                            <Col md={9}>
                              <div>Inspected by:</div>
                              <div>
                                <img
                                  src="assets/img/inspected.png"
                                  alt="Inspected"
                                  className="qc-sign-1"
                                />
                              </div>

                              <div>
                                Date:
                                <span>
                                  &nbsp;
                                  {report?.reportDate ? report.reportDate : getCurrentDate()}
                                </span>
                              </div>
                            </Col>

                            <Col md={3}>
                              <div>Approved by:</div>
                              <div>
                                <img
                                  src="assets/img/approved.png"
                                  alt="Approved"
                                  className="qc-sign-2"
                                />
                              </div>
                              <div>
                                Date:
                                <span>
                                  &nbsp;
                                  {report?.reportDate ? report.reportDate : getCurrentDate()}
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  </Col>
                  <div className="report-title-line com">
                  {(() => {
                    const footer = getJobcardReportFooter({
                      jobcard: report?.jobcard,
                      revisingMetadata,
                      fixedReportTitle: QUALITY_CERTIFICATE_REPORT_TITLE,
                    });

                    return (
                      <>
                        <Col xs={12} sm={6} md={6} lg={6}>
                          {footer.reportTitle}
                        </Col>
                        <Col xs={12} sm={3} md={3} lg={3}>
                          Rev No: {footer.reviseNumber}
                        </Col>
                        <Col xs={12} sm={3} md={3} lg={3} className="text-end">
                          Date: {footer.reviseDate}
                        </Col>
                      </>
                    );
                  })()}
                  </div>
                </React.Fragment>
              );
            })}
          </Row>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PrintMultipleQCReports;
